import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Upload, Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessProfileSettings({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    address: '',
    phone: '',
    email: '',
    npwp: '',
    website: '',
    logo_url: ''
  });

  useEffect(() => {
    fetchBusinessProfile();
  }, []);

  const fetchBusinessProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/business-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.profile) {
        setFormData(response.data.profile);
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
      toast.error('Gagal memuat profil bisnis');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/business-profile/upload-logo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFormData(prev => ({
        ...prev,
        logo_url: response.data.logo_url
      }));

      toast.success('Logo berhasil diupload');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.detail || 'Gagal upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent submit while loading or saving
    if (loading || saving) {
      console.log('Form is still loading or saving, please wait...');
      return;
    }

    // Validation
    if (!formData.business_name.trim()) {
      toast.error('Nama bisnis wajib diisi');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Alamat wajib diisi');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('No. Telepon wajib diisi');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Token tidak ditemukan. Silakan login kembali.');
        setSaving(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/business-profile`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 10000 // 10 second timeout
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Profil bisnis berhasil disimpan');
        // Small delay before closing to show success message
        setTimeout(() => {
          if (onClose) onClose();
        }, 500);
      } else {
        toast.error('Gagal menyimpan profil bisnis');
        setSaving(false);
      }
    } catch (error) {
      console.error('Error saving business profile:', error);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timeout. Silakan coba lagi.');
      } else if (error.response) {
        // Server responded with error
        const errorMsg = error.response.data?.detail || error.response.data?.message || 'Gagal menyimpan profil bisnis';
        toast.error(errorMsg);
      } else if (error.request) {
        // Request made but no response
        toast.error('Tidak dapat terhubung ke server. Cek koneksi internet Anda.');
      } else {
        // Other errors
        toast.error('Terjadi kesalahan. Silakan coba lagi.');
      }
      
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Profil Bisnis</h2>
              <p className="text-blue-100 text-sm mt-1">Kelola informasi bisnis Anda</p>
            </div>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-b-lg shadow-lg">
        {/* Logo Upload */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Logo Bisnis
          </label>
          <div className="flex items-center gap-4">
            {/* Logo Preview */}
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
              {formData.logo_url ? (
                <img
                  src={formData.logo_url}
                  alt="Business Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <Building2 className="h-12 w-12 text-gray-400" />
              )}
            </div>

            {/* Upload Button */}
            <div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                <Upload className="h-4 w-4" />
                {uploading ? 'Uploading...' : 'Upload Logo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-gray-500 mt-2">
                PNG, JPG, atau JPEG. Max 2MB
              </p>
            </div>
          </div>
        </div>

        {/* Business Name */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Nama Bisnis <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="business_name"
            value={formData.business_name}
            onChange={handleInputChange}
            placeholder="Contoh: PS Rental Budi Gaming"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* Address */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Alamat Kantor Pusat <span className="text-red-500">*</span>
          </label>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder="Alamat lengkap kantor pusat"
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* Phone */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            No. Telepon <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="0812-3456-7890"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* Email (Optional) */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Email <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="info@bisnis.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* NPWP (Optional) */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            NPWP <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="text"
            name="npwp"
            value={formData.npwp}
            onChange={handleInputChange}
            placeholder="XX.XXX.XXX.X-XXX.XXX"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Website (Optional) */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Website <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="url"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            placeholder="https://www.bisnis.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Batal
            </button>
          )}
          <button
            type="submit"
            disabled={loading || saving || uploading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Menyimpan...' : loading ? 'Loading...' : 'Simpan Profil'}
          </button>
        </div>
      </form>
    </div>
  );
}
